import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, X } from "lucide-react";
import { post, errorMessage } from "@/lib/api";
import { Mascot } from "@/pages/student/Mascot";

/*
 * "Try it yourself" from the demo: name a course, then start from the
 * standard template or blank. This is the one real write in the demo; the
 * new course lands under My courses and opens in the course builder.
 */

type Start = "template" | "blank";

const TEMPLATE_PARTS: { tag: string; tone: string; text: string }[] = [
  { tag: "Video", tone: "teal", text: "An intro video and a slide study" },
  { tag: "4 lessons", tone: "violet", text: "Reading, audio, key terms, discussion, homework, quiz" },
  { tag: "Exam", tone: "red", text: "After the lessons" },
  { tag: "Project", tone: "amber", text: "Plus one capstone project for the course" },
];

export default function TryItDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  /** Called with the new course id once it exists (template applied or not). */
  onCreated: (courseId: string, templateFailed: boolean) => void;
}) {
  const [name, setName] = useState("My first course");
  const [start, setStart] = useState<Start>("template");
  const [making, setMaking] = useState(false);
  const [error, setError] = useState("");
  const nameRef = useRef<HTMLInputElement | null>(null);
  const trimmed = name.trim();

  useEffect(() => {
    nameRef.current?.select();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !making) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [making, onClose]);

  const create = async () => {
    if (!trimmed || making) return;
    setMaking(true);
    setError("");
    try {
      const created = await post<{ success: boolean; data: { id: string } }>("/api/courses", {
        title: trimmed,
        description: "Started from the AcuityLearn demo.",
        isMaster: false,
      });
      const id = created.data.id;
      let templateFailed = false;
      if (start === "template") {
        try {
          await post(`/api/courses/${id}/template`, {});
        } catch {
          // The course still exists; the builder offers the template again.
          templateFailed = true;
        }
      }
      onCreated(id, templateFailed);
    } catch (err) {
      setError(errorMessage(err));
      setMaking(false);
    }
  };

  return createPortal(
    <div className="al-try-backdrop" onClick={() => (making ? undefined : onClose())} role="presentation">
      <section
        className="al-try"
        role="dialog"
        aria-modal="true"
        aria-labelledby="al-try-title"
        onClick={(event) => event.stopPropagation()}
      >
        {!making ? (
          <button type="button" className="al-try-close" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        ) : null}

        {making ? (
          <div className="al-try-making" role="status">
            <Mascot pose="graduation" size={132} />
            <h2 id="al-try-title">Setting up {trimmed}…</h2>
            <p>
              {start === "template"
                ? "Adding 9 units with lessons, videos and exams. Then we open the course builder."
                : "Making an empty course. Then we open the course builder."}
            </p>
            <span className="al-try-bar" aria-hidden="true">
              <span />
            </span>
          </div>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void create();
            }}
          >
            <div className="al-try-head">
              <span className="al-try-butler">
                <Mascot pose="laptop" size={92} />
              </span>
              <div>
                <p className="al-try-eyebrow">Try it yourself</p>
                <h2 id="al-try-title">Let's make your first course</h2>
                <p className="al-try-lede">Name it, pick how you want to start, and you'll land in the course builder.</p>
              </div>
            </div>

            <label className="al-try-label" htmlFor="al-try-name">
              Course name
            </label>
            <input
              id="al-try-name"
              ref={nameRef}
              className="al-try-input"
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
              placeholder="For example, Biology 1"
              required
            />

            <p className="al-try-label" id="al-try-start">
              How do you want to start?
            </p>
            <div className="al-try-cards" role="radiogroup" aria-labelledby="al-try-start">
              <button
                type="button"
                role="radio"
                aria-checked={start === "template"}
                className={`al-try-card${start === "template" ? " is-on" : ""}`}
                onClick={() => setStart("template")}
              >
                <span className="al-try-card-top">
                  <span className="al-try-radio" aria-hidden="true" />
                  <strong>Start from our template</strong>
                  <span className="al-try-rec">Recommended</span>
                </span>
                <span className="al-try-card-text">The structure is set up for you. Fill in your own content.</span>
                <span className="al-try-parts">
                  <span className="al-try-parts-cap">9 units, each with</span>
                  {TEMPLATE_PARTS.map((part) => (
                    <span key={part.tag} className="al-try-part">
                      <span className={`al-try-tag al-try-tag--${part.tone}`}>{part.tag}</span>
                      {part.text}
                    </span>
                  ))}
                </span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={start === "blank"}
                className={`al-try-card${start === "blank" ? " is-on" : ""}`}
                onClick={() => setStart("blank")}
              >
                <span className="al-try-card-top">
                  <span className="al-try-radio" aria-hidden="true" />
                  <strong>Start blank</strong>
                </span>
                <span className="al-try-card-text">An empty course. Add units, lessons and items your own way.</span>
                <span className="al-try-empty">
                  <span className="al-try-plus" aria-hidden="true">
                    <Plus size={18} />
                  </span>
                  Add your first unit
                </span>
              </button>
            </div>

            {error ? (
              <p className="al-try-error" role="alert">
                {error}
              </p>
            ) : null}

            <div className="al-try-foot">
              <span className="al-try-hint">You can add or remove anything later. It's saved under My courses.</span>
              <button type="button" className="al-demo-btn al-demo-btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="al-demo-btn" disabled={!trimmed}>
                {start === "template" ? "Create from template" : "Create blank course"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>,
    document.body,
  );
}
